import {
	createContext,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Transition, TransitionChild } from '@headlessui/react';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * A side panel with drill-down navigation. Instead of stacking separate panels
 * on top of each other (like SidePanel), nested <SlidingPanelPage>s slide in
 * within the same panel while the page underneath shifts to the left.
 *
 * <SlidingSidePanel isOpen={open} onClose={() => setOpen(false)} title="Client">
 *     <button onClick={() => setShowForms(true)}>Forms</button>
 *     <SlidingPanelPage isOpen={showForms} onClose={() => setShowForms(false)} title="Forms">
 *         ...can contain further SlidingPanelPages
 *     </SlidingPanelPage>
 * </SlidingSidePanel>
 */

const SLIDE_MS = 300;

const SlidingPanelContext = createContext(null);

export function useSlidingPanel() {
	return useContext(SlidingPanelContext);
}

function PanelPageLayout({
	title,
	headerLeftContent,
	onBack,
	onCloseAll,
	showSave,
	onSave,
	onCancel,
	saveLabel,
	scrollRef,
	children,
}) {
	return (
		<div className="flex h-full flex-col divide-y divide-gray-200 bg-gray-100">
			<div
				className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6"
				ref={scrollRef}
			>
				<div className="px-4 sm:px-6">
					<div
						className={`flex items-start justify-between ${!title ? '-mb-10' : ''}`}
					>
						{headerLeftContent}
						<div className="flex items-center">
							{onBack && (
								<button
									type="button"
									className="text-primary-700"
									onClick={onBack}
								>
									<span className="sr-only">Back</span>
									<ChevronLeftIcon
										className="h-5 w-5 mr-2"
										aria-hidden="true"
									/>
								</button>
							)}
							<div className="text-base font-semibold leading-6 text-gray-900">
								{title}
							</div>
						</div>

						<div className="ml-3 flex h-7 items-center">
							<button
								type="button"
								className="relative rounded-md z-10 text-gray-800 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
								onClick={onCloseAll}
							>
								<span className="absolute -inset-2.5" />
								<span className="sr-only">Close panel</span>
								<XMarkIcon className="h-6 w-6" aria-hidden="true" />
							</button>
						</div>
					</div>
				</div>
				<div
					className={`relative ${title ? 'mt-6' : ''} flex-1 px-4 sm:px-6`}
				>
					{children}
				</div>
			</div>
			{showSave && (
				<div className="bg-primary-700 flex flex-shrink-0 justify-between p-6">
					<button
						type="submit"
						className="p-2 text-primary-700 hover:bg-primary-100 rounded-full bg-white flex-1 mr-4"
						onClick={ev => {
							ev.preventDefault();
							onSave();
						}}
					>
						{saveLabel || 'Save'}
					</button>
					<button
						type="button"
						className="hover:bg-primary-100 hover:border-primary-100 hover:text-primary-700 bg-primary-700 text-white border-2 border-white rounded-full p-2 flex-1"
						onClick={ev => {
							ev.preventDefault();
							onCancel();
						}}
					>
						Cancel
					</button>
				</div>
			)}
		</div>
	);
}

function layerClass(phase, isTop) {
	if (phase === 'entering' || phase === 'leaving') {
		return 'translate-x-full';
	}
	return isTop ? 'translate-x-0' : '-translate-x-1/3';
}

function Layer({ phase, isTop, children }) {
	const covered = !isTop && phase === 'open';
	// Broader than covered: a layer sliding out, or one that ended up beneath
	//  another while still sliding in, is just as unreachable as a settled one
	//  underneath. topId is taken from the pages that aren't leaving, so this
	//  is true of every layer except the one being used.
	const isInactive = !isTop;
	return (
		<div
			className={`absolute inset-0 transform transition-transform duration-300 ease-in-out ${layerClass(phase, isTop)} ${covered ? 'pointer-events-none' : ''}`}
			aria-hidden={covered || undefined}
			// Empty string rather than true: React 18 warns about boolean
			//  values on attributes it doesn't know, and inert is one
			inert={isInactive ? '' : undefined}
		>
			{children}
		</div>
	);
}

const SlidingSidePanel = forwardRef(
	(
		{
			isOpen,
			onClose,
			children,
			title,
			widthClass,
			onSave,
			showSave,
			onCancel,
			saveLabel,
			headerLeftContent,
			showBackArrow,
			isHidden,
		},
		ref,
	) => {
		// Nested pages, ordered bottom -> top. phase: entering | open | leaving
		const [pages, setPages] = useState([]);
		const pagesRef = useRef(pages);
		pagesRef.current = pages;
		const isOpenRef = useRef(isOpen);
		isOpenRef.current = isOpen;
		const callbacks = useRef(new Map());
		const seqRef = useRef(0);
		const rootScrollRef = useRef();

		const removePage = useCallback(id => {
			setPages(prev => prev.filter(p => p.id !== id));
			callbacks.current.get(id)?.onRemoved();
			callbacks.current.delete(id);
		}, []);

		const openPage = useCallback((id, depth, node, cbs) => {
			callbacks.current.set(id, cbs);
			const seq = ++seqRef.current;
			setPages(prev => {
				if (prev.some(p => p.id === id)) {
					// Re-opened while sliding out
					return prev.map(p =>
						p.id === id ? { ...p, phase: 'open' } : p,
					);
				}
				return [...prev, { id, depth, seq, node, phase: 'entering' }].sort(
					(a, b) => a.depth - b.depth || a.seq - b.seq,
				);
			});
		}, []);

		const closePage = useCallback(
			id => {
				// Closing the whole panel: leave pages in place while it slides away
				if (!isOpenRef.current) {
					return;
				}
				const idx = pagesRef.current.findIndex(p => p.id === id);
				if (idx === -1) {
					return;
				}
				// Anything above this page goes with it
				pagesRef.current
					.slice(idx + 1)
					.forEach(p => callbacks.current.get(p.id)?.onClose());
				setPages(prev =>
					prev.map(p => (p.id === id ? { ...p, phase: 'leaving' } : p)),
				);
				setTimeout(() => {
					if (
						pagesRef.current.find(p => p.id === id)?.phase === 'leaving'
					) {
						removePage(id);
					}
				}, SLIDE_MS);
			},
			[removePage],
		);

		const unregisterPage = useCallback(id => {
			setPages(prev => prev.filter(p => p.id !== id));
			callbacks.current.delete(id);
		}, []);

		const popToRoot = useCallback(() => {
			[...pagesRef.current]
				.reverse()
				.forEach(p => callbacks.current.get(p.id)?.onClose());
			rootScrollRef.current?.scrollTo(0, 0);
		}, []);

		const closeAll = useCallback(() => {
			[...pagesRef.current]
				.reverse()
				.forEach(p => callbacks.current.get(p.id)?.onClose());
			onClose();
		}, [onClose]);

		// Slide in newly added pages on the frame after they're rendered off-screen
		useEffect(() => {
			if (!pages.some(p => p.phase === 'entering')) {
				return;
			}
			let raf2;
			const raf1 = requestAnimationFrame(() => {
				raf2 = requestAnimationFrame(() => {
					setPages(prev =>
						prev.map(p =>
							p.phase === 'entering' ? { ...p, phase: 'open' } : p,
						),
					);
				});
			});
			return () => {
				cancelAnimationFrame(raf1);
				cancelAnimationFrame(raf2);
			};
		}, [pages]);

		useImperativeHandle(ref, () => ({
			close: closeAll,
			closeAllAbove: popToRoot,
			popToRoot,
		}));

		const context = useMemo(
			() => ({
				depth: 0,
				openPage,
				closePage,
				unregisterPage,
				popToRoot,
				closeAll,
			}),
			[openPage, closePage, unregisterPage, popToRoot, closeAll],
		);

		const openPages = pages.filter(p => p.phase !== 'leaving');
		const topId = openPages.length ? openPages[openPages.length - 1].id : null;

		return (
			<Transition show={isOpen}>
				<div className="relative z-10">
					<div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
						<TransitionChild
							enter="transform transition ease-in-out duration-500"
							enterFrom="translate-x-full"
							enterTo="translate-x-0"
							leave="transform transition ease-in-out duration-500"
							leaveFrom="translate-x-0"
							leaveTo="translate-x-full"
							afterLeave={() => {
								pagesRef.current.forEach(p => removePage(p.id));
							}}
						>
							<div
								className={`pointer-events-auto side-panel ${widthClass ? widthClass : 'w-screen max-w-md'}`}
								// Inline rather than a class: TransitionChild
								//  merges classes onto this element instead of
								//  letting React replace them, so a class added
								//  here is never taken off again
								style={{
									pointerEvents: isHidden ? 'none' : undefined,
								}}
							>
								{/* The surface slides away rather than the panel
								    unmounting, so whatever asked to be got out of
								    the way keeps its state. This element is
								    React's alone, so its classes do come off. */}
								<div
									className={`relative h-full overflow-hidden shadow-xl transform transition-transform duration-300 ease-in-out ${isHidden ? 'translate-x-full' : 'translate-x-0'}`}
									aria-hidden={isHidden || undefined}
									inert={isHidden ? '' : undefined}
								>
									<SlidingPanelContext.Provider value={context}>
										<Layer phase="open" isTop={topId === null}>
											<PanelPageLayout
												title={title}
												headerLeftContent={headerLeftContent}
												onBack={showBackArrow ? onClose : null}
												onCloseAll={closeAll}
												showSave={showSave}
												onSave={onSave}
												onCancel={onCancel}
												saveLabel={saveLabel}
												scrollRef={rootScrollRef}
											>
												{children}
											</PanelPageLayout>
										</Layer>
									</SlidingPanelContext.Provider>
									{pages.map(p => (
										<Layer
											key={p.id}
											phase={p.phase}
											isTop={p.id === topId}
										>
											<div
												className="h-full"
												ref={el => {
													if (el && p.node.parentNode !== el) {
														el.appendChild(p.node);
													}
												}}
											/>
										</Layer>
									))}
								</div>
							</div>
						</TransitionChild>
					</div>
				</div>
			</Transition>
		);
	},
);

export function SlidingPanelPage({
	isOpen,
	onClose,
	children,
	title,
	onSave,
	showSave,
	onCancel,
	saveLabel,
	headerLeftContent,
}) {
	const ctx = useContext(SlidingPanelContext);
	if (!ctx) {
		throw new Error('SlidingPanelPage must be rendered inside a SlidingSidePanel');
	}
	const [id] = useState(() => '' + new Date().getTime() + Math.random());
	const [node] = useState(() => {
		const el = document.createElement('div');
		el.className = 'h-full';
		return el;
	});
	// Stays true while sliding out so the content doesn't vanish mid-animation
	const [mounted, setMounted] = useState(false);
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		if (isOpen) {
			setMounted(true);
			ctx.openPage(id, ctx.depth + 1, node, {
				onClose: () => onCloseRef.current?.(),
				onRemoved: () => setMounted(false),
			});
		} else {
			ctx.closePage(id);
		}
	}, [isOpen]);

	useEffect(() => {
		return () => {
			ctx.unregisterPage(id);
		};
	}, []);

	const childContext = useMemo(
		() => ({ ...ctx, depth: ctx.depth + 1 }),
		[ctx],
	);

	if (!mounted) {
		return null;
	}

	return createPortal(
		<SlidingPanelContext.Provider value={childContext}>
			<PanelPageLayout
				title={title}
				headerLeftContent={headerLeftContent}
				onBack={onClose}
				onCloseAll={ctx.closeAll}
				showSave={showSave}
				onSave={onSave}
				onCancel={onCancel}
				saveLabel={saveLabel}
			>
				{children}
			</PanelPageLayout>
		</SlidingPanelContext.Provider>,
		node,
	);
}

export default SlidingSidePanel;
